// React Query hooks for the WB customers surface (Phase C3).
//
// Pairs with /api/v1/wb/customer-settings/* :
//   GET  /                  WB-lensed customer list (paginated + search)
//   GET  /:customer_enc_id  per-customer WB settings (defaults if no row)
//   PUT  /:customer_enc_id  upsert WB settings
//
// The URL key throughout is the *customer's* enc_id (the settings table is
// keyed on customer_id, no surrogate id). DTOs mirror
// server/src/modules/wb/customer-settings/types.ts exactly.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Client-side types (mirror the server DTOs) ----------------------------

export type WbAccountStatus = 'active' | 'on_hold' | 'closed';

export interface WbCustomerListItem {
  readonly customer_enc_id: string;
  readonly uuid: string;
  readonly name: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly account_status: WbAccountStatus;
  readonly credit_limit: number | null;
  readonly on_hold: boolean;
  readonly has_settings: boolean;
}

export interface Pagination {
  readonly page: number;
  readonly per_page: number;
  readonly total: number;
  readonly total_pages: number;
}

export interface WbPriceBandRef {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
}

export interface WbHaulierRef {
  readonly enc_id: string;
  readonly name: string;
}

export interface WbVehicleRef {
  readonly enc_id: string;
  readonly registration: string;
}

export interface WbEwcCodeRef {
  readonly code: string;
  readonly description: string;
}

export interface WbCustomerSettings {
  readonly customer_enc_id: string;
  readonly price_band: WbPriceBandRef | null;
  readonly credit_limit: number | null;
  readonly account_status: WbAccountStatus;
  readonly on_hold_reason: string | null;
  readonly payment_terms_days: number | null;
  readonly default_haulier: WbHaulierRef | null;
  readonly default_vehicle: WbVehicleRef | null;
  readonly default_ewc_code: WbEwcCodeRef | null;
  readonly notes: string | null;
}

/** Read-only core fields of the central customer row, embedded in the
 *  settings GET so the detail page can render Section A + deep-link to the
 *  central customer edit page without a second request. */
export interface WbCustomerCore {
  readonly customer_enc_id: string;
  readonly uuid: string;
  readonly name: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly address: string | null;
}

export interface WbCustomerSettingsResult {
  readonly settings: WbCustomerSettings;
  readonly customer: WbCustomerCore;
}

/**
 * PUT body — every field optional. FK refs (price band / haulier / vehicle)
 * are sent as enc_ids; default_ewc_code is the raw 6-digit code string.
 * `null` explicitly clears a field; omitting it leaves it untouched.
 */
export interface UpdateWbCustomerSettingsInput {
  price_band_id?: string | null;
  credit_limit?: number | null;
  account_status?: WbAccountStatus;
  on_hold_reason?: string | null;
  payment_terms_days?: number | null;
  default_haulier_id?: string | null;
  default_vehicle_id?: string | null;
  default_ewc_code?: string | null;
  notes?: string | null;
}

export interface ListWbCustomersParams {
  page?: number;
  per_page?: number;
  search?: string;
}

interface ListWbCustomersResponse {
  customers: WbCustomerListItem[];
  pagination: Pagination;
}

// -- Helpers ----------------------------------------------------------------

function buildQueryString(params: ListWbCustomersParams): string {
  const sp = new URLSearchParams();
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.per_page !== undefined) sp.set('per_page', String(params.per_page));
  if (params.search !== undefined && params.search !== '') {
    sp.set('search', params.search);
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

// -- Queries ----------------------------------------------------------------

export function useWbCustomers(params: ListWbCustomersParams = {}) {
  return useQuery<ListWbCustomersResponse>({
    queryKey: queryKeys.wb.customers(params as Record<string, unknown>),
    queryFn: async () => {
      const qs = buildQueryString(params);
      const res = await apiClient.get<ListWbCustomersResponse>(
        `${endpoints.wb.customers}${qs}`,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useWbCustomerSettings(customerEncId: string | undefined) {
  const enabled = !!customerEncId;
  return useQuery<WbCustomerSettingsResult>({
    queryKey: queryKeys.wb.customerSettings(customerEncId ?? ''),
    queryFn: async () => {
      if (!customerEncId) {
        throw new Error('useWbCustomerSettings called without customer enc_id');
      }
      const res = await apiClient.get<WbCustomerSettingsResult>(
        endpoints.wb.customerSettings(customerEncId),
      );
      return res.data;
    },
    enabled,
    staleTime: 30_000,
  });
}

// -- Mutations --------------------------------------------------------------

export function useUpdateWbCustomerSettings(customerEncId: string) {
  const qc = useQueryClient();
  return useMutation<
    { settings: WbCustomerSettings },
    Error,
    UpdateWbCustomerSettingsInput
  >({
    mutationFn: async (body) => {
      const res = await apiClient.put<
        { settings: WbCustomerSettings },
        UpdateWbCustomerSettingsInput
      >(endpoints.wb.customerSettings(customerEncId), body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.wb.customerSettings(customerEncId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.customers() });
    },
  });
}
